import { useToast } from '@chakra-ui/react'
import { useMemo } from 'react'

import { getApiErrorMessage } from './apiError'

/**
 * Duração por tipo. O painel tinha nove valores espalhados (5000 e 3000 praticamente
 * empatados, mais 4000, 2000, 6000, 9000, 8000, 3500, 2500) — a escolha aqui é uma só:
 * erro fica mais tempo porque quase sempre traz uma descrição para ler; sucesso é
 * confirmação do que o usuário acabou de fazer e sai rápido.
 */
const TOAST_DURATION = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 4000
} as const

/** Frase única para formulário inválido — antes eram seis, uma por página. */
const INVALID_FORM_TITLE = 'Verifique os campos do formulário'

export interface IAppToast {
  success: (title: string, description?: string) => void
  /**
   * `error` recebe o erro cru do `catch`: a descrição sai do `getApiErrorMessage`, então
   * a mensagem que o backend mandou aparece em vez de um texto fixo. Passe uma string
   * quando a descrição não vier de uma request.
   */
  error: (title: string, cause?: unknown) => void
  warning: (title: string, description?: string) => void
  info: (title: string, description?: string) => void
  /** Validação de formulário no cliente, com a frase padrão do painel. */
  invalidForm: (description?: string) => void
  /** Fecha os toasts abertos — usado ao desmontar uma tela que dispara vários. */
  closeAll: () => void
}

/**
 * Toasts do painel. Toda notificação passa por aqui — não chame `useToast` direto.
 *
 * `position` não é parametrizável de propósito: 647 dos 650 toasts existentes herdavam o
 * default do Chakra, e as três exceções eram valores de CSS copiados por engano
 * (`sticky`, `absolute`), que o Chakra ignora.
 */
export function useAppToast(): IAppToast {
  const toast = useToast()

  return useMemo(() => {
    const show = (status: keyof typeof TOAST_DURATION, title: string, description?: string) =>
      toast({
        title,
        description,
        status,
        duration: TOAST_DURATION[status],
        isClosable: true
      })

    return {
      success: (title, description) => show('success', title, description),
      error: (title, cause) => show('error', title, typeof cause === 'string' ? cause : getApiErrorMessage(cause)),
      warning: (title, description) => show('warning', title, description),
      info: (title, description) => show('info', title, description),
      invalidForm: (description) => show('warning', INVALID_FORM_TITLE, description),
      closeAll: () => toast.closeAll()
    }
  }, [toast])
}
